import type {
  SubscriptionV2Variable,
  BackendNotifierConfig,
} from "@/pages/subscriptions/services/types";
import { VariableType, ChannelType } from "@/pages/subscriptions/constants";

// Type for metric config params
export type MetricParams = {
  aggregation?: string;
  comparison_type?: string;
  threshold_type?: string;
  threshold_value?: number;
  is_percentage?: boolean | null;
  date_range?: string;
};

export function extractEntityVariable(
  entity_type: string,
  category?: string
): SubscriptionV2Variable {
  // Store the category identifier for looking up display name on the frontend
  // The displayValue will be set by the frontend using the /metrics endpoint
  const displayValue = category || entity_type.replace(/s$/, "");

  return {
    id: "entities", // Note: using "entities" to match original prompt
    variableId: "entities",
    type: VariableType.ENTITY,
    value: `all_${entity_type}s`,
    displayValue, // Category identifier (will be looked up on frontend)
    config: {
      isEditable: false,
      category, // Store category for lookup
    },
  };
}

export function extractMetricVariable(metric_name: string): SubscriptionV2Variable {
  return {
    id: "metric",
    variableId: "metric",
    type: VariableType.METRIC,
    value: metric_name,
    displayValue: metric_name,
    config: { isEditable: true },
  };
}

export function extractAggregationVariable(params: MetricParams): SubscriptionV2Variable {
  const value = params.aggregation || "mean";
  const displayValue = params.aggregation || "mean";

  return {
    id: "aggregation",
    variableId: "aggregation",
    type: VariableType.AGGREGATION,
    value, // Raw value for form: "sum", "mean", etc.
    displayValue, // Will be capitalized on detail page via lookup
    config: { isEditable: true },
  };
}

export function extractChangeTypeVariable(params: MetricParams): SubscriptionV2Variable {
  const value = params.comparison_type || "relative_value";
  const displayValue = params.comparison_type || "relative_value";

  return {
    id: "change_type",
    variableId: "change_type",
    type: VariableType.CHANGE_TYPE,
    value, // Raw value: "relative_value", "absolute_value"
    displayValue, // Will be formatted on detail page
    config: { isEditable: true },
  };
}

export function extractComparisonVariable(params: MetricParams): SubscriptionV2Variable {
  const value = params.threshold_type || "exceeds";
  const displayValue = params.threshold_type || "exceeds";

  return {
    id: "comparison",
    variableId: "comparison",
    type: VariableType.COMPARISON,
    value, // Raw value: "exceeds", "below", etc.
    displayValue, // Will be formatted on detail page
    config: {
      isEditable: true,
      options: [
        { label: "Changes by", value: "changes_by" },
        { label: "Exceeds", value: "exceeds" },
        { label: "Below", value: "below" },
      ],
    },
  };
}

export function extractThresholdVariable(params: MetricParams): SubscriptionV2Variable {
  const numericValue =
    params.threshold_value !== undefined ? String(params.threshold_value) : "100";
  const isPercentage = params.is_percentage === true;

  // Add "%" if it's a percentage value
  const value = isPercentage ? `${numericValue}%` : numericValue;
  const displayValue = isPercentage ? `${numericValue}%` : numericValue;

  return {
    id: "threshold",
    variableId: "threshold",
    type: VariableType.THRESHOLD,
    value,
    displayValue,
    config: { isEditable: true },
  };
}

export function extractDateRangeVariable(params: MetricParams): SubscriptionV2Variable {
  const value = params.date_range || "previous_day";
  const displayValue = params.date_range || "previous_day";

  return {
    id: "time_period", // Note: using "time_period" to match original prompt
    variableId: "time_period",
    type: VariableType.DATE_RANGE,
    value, // Raw value: "previous_week", "previous_month", etc.
    displayValue, // Will be formatted on detail page
    config: { isEditable: true },
  };
}

type ScheduleData = {
  frequency: "daily" | "weekly" | "monthly";
  time: string;
  daysOfWeek?: string[];
  dayOfMonth?: number;
};

export function extractScheduleVariable(schedule_config?: {
  cron: string;
  timezone: string;
}): SubscriptionV2Variable {
  // Use default schedule if not provided
  if (!schedule_config) {
    return {
      id: "schedule",
      variableId: "schedule",
      type: VariableType.SCHEDULE,
      value: JSON.stringify({ frequency: "daily", time: "2:00 PM" }),
      displayValue: "Daily at 2:00 PM",
      config: { isEditable: true },
    };
  }

  // Parse cron to extract schedule info
  const parts = schedule_config.cron.split(" ");
  const [minute, hour, dayOfMonth, _month, dayOfWeek] = parts;

  let frequency: "daily" | "weekly" | "monthly" = "daily";
  let daysOfWeek: string[] = [];
  let dayOfMonthNum = 1;
  let unsupported = false;

  // Determine frequency
  if (dayOfWeek !== "*") {
    frequency = "weekly";
    const dayMap: Record<string, string> = {
      "0": "Sun",
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
    };
    daysOfWeek = dayOfWeek.split(",").map(d => dayMap[d] || "Mon");
  } else if (dayOfMonth !== "*") {
    frequency = "monthly";
    // A non-numeric / out-of-range day-of-month (e.g. a legacy "L" cron) can't
    // be represented by the schedule editor. Flag it as unsupported so the
    // schedule renders as the raw cron rather than a misleading "Monthly on
    // day 1" — consistent with the list-view formatter and the invalid-time
    // fallback below.
    const parsed = parseInt(dayOfMonth, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 31) {
      unsupported = true;
    } else {
      dayOfMonthNum = parsed;
    }
  }

  // Convert hour to 12-hour format
  const hourNum = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);

  // Handle invalid, unsupported, or wildcard values
  if (unsupported || isNaN(hourNum) || isNaN(minuteNum) || hour === "*" || minute === "*") {
    return {
      id: "schedule",
      variableId: "schedule",
      type: VariableType.SCHEDULE,
      value: JSON.stringify({ frequency: "daily", time: "12:00 AM" }),
      displayValue: schedule_config.cron,
      config: { isEditable: true },
    };
  }

  const period = hourNum >= 12 ? "PM" : "AM";
  const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
  const time = `${hour12}:${minuteNum.toString().padStart(2, "0")} ${period}`;

  const scheduleData: ScheduleData = { frequency, time };
  if (frequency === "weekly") scheduleData.daysOfWeek = daysOfWeek;
  if (frequency === "monthly") scheduleData.dayOfMonth = dayOfMonthNum;

  // Create human-readable display value
  let displayValue = "";
  if (frequency === "daily") {
    displayValue = `Daily at ${time}`;
  } else if (frequency === "weekly") {
    const daysList = daysOfWeek.join(", ");
    displayValue = `Weekly on ${daysList} at ${time}`;
  } else if (frequency === "monthly") {
    displayValue = `Monthly on day ${dayOfMonthNum} at ${time}`;
  }

  return {
    id: "schedule",
    variableId: "schedule",
    type: VariableType.SCHEDULE,
    value: JSON.stringify(scheduleData),
    displayValue, // Human-readable format instead of cron
    config: { isEditable: true },
  };
}

export function extractDestinationConfig(notifier_config: BackendNotifierConfig[]) {
  const channels: ChannelType[] = [];
  const email_recipients: string[] = [];
  const slack_channels: string[] = [];

  notifier_config.forEach(notifier => {
    if (notifier.type === ChannelType.EMAIL) {
      if (!channels.includes(ChannelType.EMAIL)) channels.push(ChannelType.EMAIL);
      if (notifier.recipients) {
        email_recipients.push(...notifier.recipients);
      }
    } else if (notifier.type === ChannelType.SLACK) {
      if (!channels.includes(ChannelType.SLACK)) channels.push(ChannelType.SLACK);
      if (notifier.channel) {
        slack_channels.push(notifier.channel);
      }
    }
  });

  return {
    channels,
    email_recipients: Array.from(new Set(email_recipients)),
    slack_channels: Array.from(new Set(slack_channels)),
  };
}
